'use client';

import { PodExplodeDialog } from '@slims/ui';
import { useCallback, useMemo, useState } from 'react';
import { usePodLayers } from '@/hooks/use-pod-layers';

/**
 * Cablaggio dell'esploso per POD: tiene il giorno selezionato, chiama l'hook e
 * rende la vista. Nessuna presentazione — quella sta in `PodExplodeDialog`.
 *
 * Pensato per essere agganciato a qualunque grafico o vista energetica: la vista
 * chiamante passa l'identità del punto di misura e questo componente si occupa
 * del resto.
 */
export interface PodExplodeTarget {
	meterId: string;
	/** Codice del punto di misura: il POD per un M1, l'UPN per un M2 */
	puntoMisura: string;
	/** Codice POD del punto di connessione */
	pod: string;
	/** Direzione leggibile, es. 'Immissione (M2)' */
	direzione: string;
}

export interface PodExplodeProps {
	/** Configurazione del pannello che apre il dettaglio. */
	orgId?: string;
	/** `null` = chiuso. Aprire l'esploso significa impostare un target. */
	target: PodExplodeTarget | null;
	onClose: () => void;
	/** Giorno iniziale 'YYYY-MM-DD'; default: ieri, l'ultimo giorno civile chiuso. */
	initialDay?: string;
}

/** 'YYYY-MM-DD' del giorno civile italiano spostato di `offset` giorni. */
function romeDay(offset = 0): string {
	const now = new Date();
	// Mezzogiorno UTC come ancora: sta dentro il giorno civile italiano
	// qualunque sia l'offset del fuso, quindi lo spostamento non slitta ai cambi ora.
	const noon = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 12);
	return new Date(noon + offset * 86_400_000).toISOString().slice(0, 10);
}

const DATE_FMT = new Intl.DateTimeFormat('it-IT', {
	weekday: 'long',
	day: 'numeric',
	month: 'long',
	year: 'numeric',
});

export function PodExplode({ target, onClose, initialDay, orgId }: PodExplodeProps) {
	// L'ultimo giorno civile chiuso: oggi è ancora nel buffer e incompleto per
	// costruzione, quindi partire da lì darebbe una curva tronca.
	const [day, setDay] = useState(() => initialDay ?? romeDay(-1));

	// Il componente resta montato fra un'apertura e l'altra (con `target` a null
	// rende null), quindi l'inizializzatore di useState gira una volta sola:
	// senza questo, aprire un secondo POD mostrerebbe il giorno scelto per il
	// primo. Ricalcolo durante il render invece che in un effetto, così non c'è
	// un frame con la coppia (POD nuovo, giorno vecchio).
	const targetKey = target ? `${target.meterId}|${initialDay ?? ''}` : null;
	const [seenKey, setSeenKey] = useState<string | null>(targetKey);
	if (targetKey !== seenKey) {
		setSeenKey(targetKey);
		// Anche la chiusura azzera: riaprire lo stesso POD riparte dal giorno
		// iniziale invece che da dove l'utente aveva navigato la volta prima.
		if (targetKey !== null) setDay(initialDay ?? romeDay(-1));
	}

	const { points, coverage, isLoading } = usePodLayers({
		orgId,
		meterId: target?.meterId ?? null,
		day,
		enabled: target != null,
	});

	const shift = useCallback((delta: number) => {
		setDay((d) => {
			const [y, m, dd] = d.split('-').map(Number);
			const noon = Date.UTC(y!, m! - 1, dd!, 12) + delta * 86_400_000;
			return new Date(noon).toISOString().slice(0, 10);
		});
	}, []);

	const giorno = useMemo(() => {
		const [y, m, dd] = day.split('-').map(Number);
		return DATE_FMT.format(new Date(Date.UTC(y!, m! - 1, dd!, 12)));
	}, [day]);

	if (!target) return null;

	return (
		<PodExplodeDialog
			open
			onOpenChange={(o) => {
				if (!o) onClose();
			}}
			puntoMisura={target.puntoMisura}
			pod={target.pod}
			direzione={target.direzione}
			giorno={giorno}
			data={points}
			coverage={coverage}
			loading={isLoading}
			onPrevDay={() => shift(-1)}
			onNextDay={() => shift(1)}
			nextDayDisabled={day >= romeDay(0)}
		/>
	);
}
